import type { ToolPart } from '@cotera/sdk/core';

/**
 * True when a sub-agent's tool call actually failed.
 *
 * Checks the *value* of `output.error`, not just the key: several tools carry
 * an `error` field on every result (e.g. `coco/workflow-*` returns
 * `{ ok, status, error: null, ... }` both while pending and on success), so a
 * bare `'error' in output` check misreports those calls as failures.
 */
export function toolPartFailed(part: ToolPart): boolean {
  // A still-pending async call (e.g. a running workflow) hasn't failed yet.
  if (part.isIncomplete()) {
    return false;
  }
  if (part.isError()) {
    return true;
  }
  if (part.isWorkflowToolCall()) {
    return part.phase === 'failed' || part.phase === 'error';
  }
  const out: unknown = part.getOutput();
  if (typeof out !== 'object' || out === null || !('error' in out)) {
    return false;
  }
  const error: unknown = out.error;
  return error !== null && error !== undefined && error !== '';
}

/** True when the most recent tool call in the list failed. */
export function lastToolPartFailed(parts: ToolPart[]): boolean {
  const last = parts[parts.length - 1];
  return last !== undefined && toolPartFailed(last);
}
